import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

export const fetchWholesaleCustomers = createAsyncThunk(
  "wholesale/fetchCustomers",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiFetch("/api/product/wholesale-customers/", { method: "GET" });
      return Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки клиентов" });
    }
  }
);

export const createWholesaleCustomer = createAsyncThunk(
  "wholesale/createCustomer",
  async (
    {
      name,
      phone,
      address,
      notes,
      default_discount_type,
      default_discount_value,
    },
    { rejectWithValue }
  ) => {
    try {
      const body = {
        name,
        phone: phone || "",
        address: address || "",
        notes: notes || "",
      };
      if (default_discount_type != null && default_discount_type !== "") {
        body.default_discount_type = default_discount_type;
      }
      if (default_discount_value != null && default_discount_value !== "") {
        body.default_discount_value = String(default_discount_value);
      }
      return await apiFetch("/api/product/wholesale-customers/", {
        method: "POST",
        body,
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка создания клиента" });
    }
  }
);

export const updateWholesaleCustomer = createAsyncThunk(
  "wholesale/updateCustomer",
  async ({ customerId, ...fields }, { rejectWithValue }) => {
    try {
      const body = {};
      if (fields.name != null) body.name = fields.name;
      if (fields.phone != null) body.phone = fields.phone;
      if (fields.address != null) body.address = fields.address;
      if (fields.notes != null) body.notes = fields.notes;
      if (fields.is_active != null) body.is_active = fields.is_active;
      if (fields.default_discount_type != null) body.default_discount_type = fields.default_discount_type;
      if (fields.default_discount_value != null) body.default_discount_value = String(fields.default_discount_value);
      return await apiFetch(`/api/product/wholesale-customers/${customerId}/`, {
        method: "PATCH",
        body,
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка сохранения клиента" });
    }
  }
);

export const deleteWholesaleCustomer = createAsyncThunk(
  "wholesale/deleteCustomer",
  async (customerId, { rejectWithValue }) => {
    try {
      await apiFetch(`/api/product/wholesale-customers/${customerId}/`, { method: "DELETE" });
      return customerId;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка удаления клиента" });
    }
  }
);

export const fetchCustomerHistory = createAsyncThunk(
  "wholesale/fetchCustomerHistory",
  async (customerId, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/wholesale-customers/${customerId}/history/`, { method: "GET" });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки истории" });
    }
  }
);

export const fetchWholesaleSale = createAsyncThunk(
  "wholesale/fetchSale",
  async (saleId, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/wholesale-sales/${saleId}/`, { method: "GET" });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки продажи" });
    }
  }
);

export const createWholesaleSale = createAsyncThunk(
  "wholesale/createSale",
  async ({ customer_id, store_id, notes }, { rejectWithValue }) => {
    try {
      return await apiFetch("/api/product/wholesale-sales/", {
        method: "POST",
        body: { customer_id, store_id, notes: notes || "" },
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка создания продажи" });
    }
  }
);

export const addWholesaleItem = createAsyncThunk(
  "wholesale/addItem",
  async ({ saleId, product_id, quantity, wholesale_price, manual_discount }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/wholesale-sales/${saleId}/add-item/`, {
        method: "POST",
        body: {
          product_id: Number(product_id),
          quantity: String(quantity),
          wholesale_price: String(wholesale_price),
          manual_discount: String(manual_discount || "0"),
        },
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка добавления товара" });
    }
  }
);

export const updateWholesaleItem = createAsyncThunk(
  "wholesale/updateItem",
  async ({ itemId, quantity, wholesale_price, manual_discount }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/wholesale-sale-items/${itemId}/`, {
        method: "PATCH",
        body: {
          quantity: String(quantity),
          wholesale_price: String(wholesale_price),
          manual_discount: String(manual_discount || "0"),
        },
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка обновления товара" });
    }
  }
);

export const removeWholesaleItem = createAsyncThunk(
  "wholesale/removeItem",
  async (itemId, { rejectWithValue }) => {
    try {
      await apiFetch(`/api/product/wholesale-sale-items/${itemId}/`, { method: "DELETE" });
      return itemId;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка удаления товара" });
    }
  }
);

export const updateWholesaleSaleDiscount = createAsyncThunk(
  "wholesale/updateSaleDiscount",
  async ({ saleId, order_discount_type, order_discount_value, notes }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/wholesale-sales/${saleId}/`, {
        method: "PATCH",
        body: { order_discount_type, order_discount_value: String(order_discount_value), notes: notes || "" },
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка обновления скидки" });
    }
  }
);

export const confirmWholesaleSale = createAsyncThunk(
  "wholesale/confirmSale",
  async ({ saleId, payment_mode, payment_type, initial_payment }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/wholesale-sales/${saleId}/confirm/`, {
        method: "POST",
        body: {
          payment_mode,
          payment_type,
          initial_payment: String(initial_payment || "0"),
        },
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка подтверждения продажи" });
    }
  }
);

export const createWholesalePayment = createAsyncThunk(
  "wholesale/createPayment",
  async ({ sale_id, customer_id, amount, payment_type, notes }, { rejectWithValue }) => {
    try {
      return await apiFetch("/api/product/wholesale-payments/", {
        method: "POST",
        body: {
          sale_id,
          customer_id,
          amount: String(amount),
          payment_type,
          notes: notes || "",
        },
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка создания платежа" });
    }
  }
);

export const fetchWholesaleSales = createAsyncThunk(
  "wholesale/fetchSales",
  async ({ page = 1, search = "", status = "", customerId = "" } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, page_size: 30 });
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      if (customerId) params.set("customer", customerId);
      const data = await apiFetch(`/api/product/wholesale-sales/?${params}`, { method: "GET" });
      return Array.isArray(data) ? { results: data, count: data.length } : data;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки продаж" });
    }
  }
);

export const searchProductsForWholesale = createAsyncThunk(
  "wholesale/searchProducts",
  async (query, { rejectWithValue }) => {
    try {
      const url = `/api/product/products/?search=${encodeURIComponent(query)}&page_size=20`;
      const data = await apiFetch(url, { method: "GET" });
      return Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка поиска товаров" });
    }
  }
);

const wholesaleSlice = createSlice({
  name: "wholesale",
  initialState: {
    customers: {
      list: [],
      status: "idle",
      error: null,
    },
    customerHistory: {
      data: null,
      status: "idle",
      error: null,
    },
    currentSale: {
      data: null,
      status: "idle",
      error: null,
    },
    salesList: {
      results: [],
      count: 0,
      status: "idle",
      error: null,
    },
    productSearch: {
      results: [],
      status: "idle",
      error: null,
    },
  },
  reducers: {
    clearCustomerHistory(state) {
      state.customerHistory = { data: null, status: "idle", error: null };
    },
    clearCurrentSale(state) {
      state.currentSale = { data: null, status: "idle", error: null };
    },
    clearProductSearch(state) {
      state.productSearch = { results: [], status: "idle", error: null };
    },
    resetCustomersStatus(state) {
      state.customers.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWholesaleCustomers.pending, (state) => {
        state.customers.status = "loading";
        state.customers.error = null;
      })
      .addCase(fetchWholesaleCustomers.fulfilled, (state, action) => {
        state.customers.status = "succeeded";
        state.customers.list = action.payload;
      })
      .addCase(fetchWholesaleCustomers.rejected, (state, action) => {
        state.customers.status = "failed";
        state.customers.error = extractError(action.payload);
      })

      .addCase(createWholesaleCustomer.fulfilled, (state, action) => {
        state.customers.list.unshift(action.payload);
        state.customers.status = "succeeded";
      })

      .addCase(updateWholesaleCustomer.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.customers.list.findIndex((c) => c.id === updated.id);
        if (idx >= 0) state.customers.list[idx] = updated;
        if (state.customerHistory.data?.customer?.id === updated.id) {
          state.customerHistory.data = {
            ...state.customerHistory.data,
            customer: { ...state.customerHistory.data.customer, ...updated },
          };
        }
      })

      .addCase(deleteWholesaleCustomer.fulfilled, (state, action) => {
        const customerId = action.payload;
        const idx = state.customers.list.findIndex((c) => c.id === customerId);
        if (idx >= 0) state.customers.list[idx] = { ...state.customers.list[idx], is_active: false };
        if (state.customerHistory.data?.customer?.id === customerId) {
          state.customerHistory.data = {
            ...state.customerHistory.data,
            customer: { ...state.customerHistory.data.customer, is_active: false },
          };
        }
      })

      .addCase(fetchCustomerHistory.pending, (state) => {
        state.customerHistory.status = "loading";
        state.customerHistory.error = null;
      })
      .addCase(fetchCustomerHistory.fulfilled, (state, action) => {
        state.customerHistory.status = "succeeded";
        state.customerHistory.data = action.payload;
      })
      .addCase(fetchCustomerHistory.rejected, (state, action) => {
        state.customerHistory.status = "failed";
        state.customerHistory.error = extractError(action.payload);
      })

      .addCase(fetchWholesaleSale.pending, (state) => {
        state.currentSale.status = "loading";
        state.currentSale.error = null;
      })
      .addCase(fetchWholesaleSale.fulfilled, (state, action) => {
        state.currentSale.status = "succeeded";
        state.currentSale.data = action.payload;
      })
      .addCase(fetchWholesaleSale.rejected, (state, action) => {
        state.currentSale.status = "failed";
        state.currentSale.error = extractError(action.payload);
      })

      .addCase(addWholesaleItem.fulfilled, (state, action) => {
        if (state.currentSale.data) {
          const sale = state.currentSale.data;
          const existing = (sale.items || []).findIndex((i) => i.id === action.payload.id);
          if (existing >= 0) {
            sale.items[existing] = action.payload;
          } else {
            sale.items = [...(sale.items || []), action.payload];
          }
        }
      })

      .addCase(updateWholesaleItem.fulfilled, (state, action) => {
        if (state.currentSale.data?.items) {
          const idx = state.currentSale.data.items.findIndex((i) => i.id === action.payload.id);
          if (idx >= 0) state.currentSale.data.items[idx] = action.payload;
        }
      })

      .addCase(removeWholesaleItem.fulfilled, (state, action) => {
        if (state.currentSale.data?.items) {
          state.currentSale.data.items = state.currentSale.data.items.filter((i) => i.id !== action.payload);
        }
      })

      .addCase(updateWholesaleSaleDiscount.fulfilled, (state, action) => {
        state.currentSale.data = action.payload;
      })

      .addCase(confirmWholesaleSale.fulfilled, (state, action) => {
        state.currentSale.data = action.payload;
      })

      .addCase(createWholesalePayment.fulfilled, (state, action) => {
        if (state.currentSale.data?.payments) {
          state.currentSale.data.payments = [...state.currentSale.data.payments, action.payload];
        }
      })

      .addCase(fetchWholesaleSales.pending, (state) => {
        state.salesList.status = "loading";
        state.salesList.error = null;
      })
      .addCase(fetchWholesaleSales.fulfilled, (state, action) => {
        state.salesList.status = "succeeded";
        state.salesList.results = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.results || []);
        state.salesList.count = action.payload?.count ?? state.salesList.results.length;
      })
      .addCase(fetchWholesaleSales.rejected, (state, action) => {
        state.salesList.status = "failed";
        state.salesList.error = extractError(action.payload);
      })

      .addCase(searchProductsForWholesale.pending, (state) => {
        state.productSearch.status = "loading";
        state.productSearch.error = null;
      })
      .addCase(searchProductsForWholesale.fulfilled, (state, action) => {
        state.productSearch.status = "succeeded";
        state.productSearch.results = action.payload;
      })
      .addCase(searchProductsForWholesale.rejected, (state, action) => {
        state.productSearch.status = "failed";
        state.productSearch.error = extractError(action.payload);
      });
  },
});

function extractError(payload) {
  if (!payload) return "Ошибка";
  if (typeof payload === "string") return payload;
  return payload.detail || payload.message || JSON.stringify(payload);
}

export const { clearCustomerHistory, clearCurrentSale, clearProductSearch, resetCustomersStatus } =
  wholesaleSlice.actions;

export const selectWholesaleCustomers = (state) => state.wholesale.customers.list;
export const selectWholesaleCustomersStatus = (state) => state.wholesale.customers.status;
export const selectWholesaleCustomersError = (state) => state.wholesale.customers.error;
export const selectCustomerHistory = (state) => state.wholesale.customerHistory.data;
export const selectCustomerHistoryStatus = (state) => state.wholesale.customerHistory.status;
export const selectCustomerHistoryError = (state) => state.wholesale.customerHistory.error;
export const selectCurrentSale = (state) => state.wholesale.currentSale.data;
export const selectCurrentSaleStatus = (state) => state.wholesale.currentSale.status;
export const selectCurrentSaleError = (state) => state.wholesale.currentSale.error;
export const selectWholesaleSalesList = (state) => state.wholesale.salesList.results;
export const selectWholesaleSalesCount = (state) => state.wholesale.salesList.count;
export const selectWholesaleSalesListStatus = (state) => state.wholesale.salesList.status;
export const selectWholesaleSalesListError = (state) => state.wholesale.salesList.error;
export const selectProductSearchResults = (state) => state.wholesale.productSearch.results;
export const selectProductSearchStatus = (state) => state.wholesale.productSearch.status;
export const selectProductSearchError = (state) => state.wholesale.productSearch.error;

export default wholesaleSlice.reducer;
